import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { getServerMastersList, previewDataPushing, runDataPushing, previewDataPulling, runDataPulling } from '../utilities/api';
import '../custom.css';

// Server <-> Client syncing for a selected client, matching
// Server\DataPushing exactly (see its docblock):
//
//   - push()/preview(): server (master) -> client. Table by table, creates
//     the table on the client first (cloning the master's schema) if it
//     doesn't exist yet, then upserts by primary key — "server wins" on
//     conflict. Every table is eligible, including GLOBAL_TABLES (shared
//     catalogs with no client_id/branch_id column at all — pushed to every
//     selected client as-is).
//   - pull()/pullPreview(): the reverse — reads CLIENT_TABLES + CLIENT_
//     BRANCH_TABLES rows FROM the client's own database and upserts them
//     onto the server, "client wins" on conflict. GLOBAL_TABLES are
//     deliberately never pullable — one client's local copy overwriting the
//     shared catalog would corrupt data used by every other client.
//
// branch_id, when given, only narrows CLIENT_BRANCH_TABLES in either
// direction — CLIENT_TABLES (client_creation/branch_creation/
// category_master) and GLOBAL_TABLES have no meaningful single-branch scope
// and are always synced in full for the client.
const GLOBAL_GROUP = {
    key: 'global',
    title: 'Global Masters',
    hint: 'Shared catalogs with no client scope at all — pushed to every selected client as-is. Never pullable back (one client’s copy would corrupt the shared catalog).',
    color: '#0ea5e9',
    icon: 'pi-globe',
    needsBranch: false,
    pushOnly: true,
    tables: [
        { value: 'uom_creation', label: 'UOM', icon: 'pi-percentage' },
        { value: 'tax_creation', label: 'Tax', icon: 'pi-percentage' },
        { value: 'platform', label: 'Platform', icon: 'pi-desktop' },
        { value: 'sections', label: 'Sections', icon: 'pi-th-large' },
        { value: 'roles', label: 'Roles', icon: 'pi-id-card' },
        { value: 'message_type', label: 'Message Type', icon: 'pi-comments' },
        { value: 'setting', label: 'Setting', icon: 'pi-cog' },
        { value: 'master', label: 'Master', icon: 'pi-database' }
    ]
};
const GROUPS = [
    {
        key: 'client',
        title: 'Client Tables',
        hint: 'The client’s own record and its branches — always synced in full.',
        color: '#f59e0b',
        icon: 'pi-building',
        needsBranch: false,
        tables: [
            { value: 'client_creation', label: 'Client', icon: 'pi-building' },
            { value: 'branch_creation', label: 'Branch', icon: 'pi-map-marker' },
            { value: 'category_master', label: 'Category Master', icon: 'pi-tags' }
        ]
    },
    {
        key: 'client_branch',
        title: 'Client + Branch Tables',
        hint: 'Pick a branch — only that branch’s rows are synced.',
        color: '#22c55e',
        icon: 'pi-sitemap',
        needsBranch: true,
        tables: [
            { value: 'product_category', label: 'Product Category', icon: 'pi-tag' },
            { value: 'product', label: 'Product', icon: 'pi-box' },
            { value: 'application_setting', label: 'Application Settings', icon: 'pi-sliders-h' },
            { value: 'masters_creation', label: 'Masters Creation', icon: 'pi-database' },
            { value: 'users', label: 'Users', icon: 'pi-users' },
            { value: 'locations', label: 'Locations', icon: 'pi-map' },
            { value: 'company_creation', label: 'Company', icon: 'pi-building' },
            { value: 'splproduct', label: 'Special Product Pricing', icon: 'pi-dollar' },
            { value: 'tables', label: 'Tables (Seating)', icon: 'pi-table' },
            { value: 'supplier', label: 'Supplier', icon: 'pi-truck' },
            { value: 'sms_authorize_final', label: 'SMS Authorization', icon: 'pi-envelope' },
            { value: 'printer_details', label: 'Printer Details', icon: 'pi-print' }
        ]
    }
];

const PUSH_GROUPS = [GLOBAL_GROUP, ...GROUPS];
const PUSH_TABLES = PUSH_GROUPS.flatMap((g) => g.tables);
const PUSH_TABLE_VALUES = PUSH_TABLES.map((t) => t.value);

const PULL_GROUPS = GROUPS; // GLOBAL_TABLES are never pullable — see docblock.
const PULL_TABLES = PULL_GROUPS.flatMap((g) => g.tables);
const PULL_TABLE_VALUES = PULL_TABLES.map((t) => t.value);
const CLIENT_TABLES_VALUES = GROUPS.find((g) => g.key === 'client').tables.map((t) => t.value);

const TABLE_META = Object.fromEntries(PUSH_GROUPS.flatMap((g) => g.tables.map((t) => [t.value, { ...t, group: g.key, color: g.color }])));

const PUSH_COLOR = '#0ea5e9';
const PULL_COLOR = '#8b5cf6';
const PUSH_STATUS_SEVERITY = { pushed: 'success', empty: 'secondary', error: 'danger' };
const PUSH_ROW_CLASS = { pushed: 'dp-row-ok', empty: 'dp-row-muted', error: 'dp-row-bad' };
const PULL_STATUS_SEVERITY = { pulled: 'success', empty: 'secondary', error: 'danger' };
const PULL_ROW_CLASS = { pulled: 'dp-row-ok', empty: 'dp-row-muted', error: 'dp-row-bad' };

const initials = (label) =>
    (label || '?')
        .replace(/\(.*\)/, '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase();

const StatTile = ({ label, value, color, icon }) => (
    <div className="dp-stat flex align-items-center gap-3 border-round shadow-1 p-3 surface-card" style={{ borderLeft: `4px solid ${color}`, minWidth: 150 }}>
        <div className="flex align-items-center justify-content-center border-round-lg" style={{ width: 40, height: 40, background: `${color}1a` }}>
            <i className={`pi ${icon}`} style={{ fontSize: '1.2rem', color }} />
        </div>
        <div>
            <div className="text-2xl font-bold line-height-1">{value}</div>
            <div className="text-color-secondary text-sm">{label}</div>
        </div>
    </div>
);

const Datapushing = () => {
    const [clients, setClients] = useState([]);
    const [allBranches, setAllBranches] = useState([]);
    const [loadingLookups, setLoadingLookups] = useState(false);

    const [client, setClient] = useState(null);

    const [pushBranch, setPushBranch] = useState(null);
    const [pushSelectedTables, setPushSelectedTables] = useState(PUSH_TABLE_VALUES);

    const [pushPreviewRows, setPushPreviewRows] = useState(null);
    const [pushPreviewLoading, setPushPreviewLoading] = useState(false);
    const [pushPreviewKey, setPushPreviewKey] = useState(null);
    const [pushConnectError, setPushConnectError] = useState(null);

    const [pushRows, setPushRows] = useState(null);
    const [pushLoading, setPushLoading] = useState(false);
    const [pushProgress, setPushProgress] = useState(null);

    const [pullBranch, setPullBranch] = useState(null);
    const [pullSelectedTables, setPullSelectedTables] = useState(PULL_TABLE_VALUES);

    const [pullPreviewRows, setPullPreviewRows] = useState(null);
    const [pullPreviewLoading, setPullPreviewLoading] = useState(false);
    const [pullPreviewKey, setPullPreviewKey] = useState(null);
    const [pullConnectError, setPullConnectError] = useState(null);

    const [pullRows, setPullRows] = useState(null);
    const [pullLoading, setPullLoading] = useState(false);
    const [pullProgress, setPullProgress] = useState(null);

    const [collapsed, setCollapsed] = useState({});
    const toggleCollapsed = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const toast = useRef(null);

    useEffect(() => {
        const loadLookups = async () => {
            setLoadingLookups(true);
            try {
                const [clientRes, branchRes] = await Promise.allSettled([getServerMastersList('client_creation'), getServerMastersList('branch_creation')]);
                const toArray = (res) => (res.status === 'fulfilled' && Array.isArray(res.value) ? res.value : res.status === 'fulfilled' && Array.isArray(res.value?.data) ? res.value.data : []);
                setClients(toArray(clientRes).map((c) => ({ label: `${c.client_name} (${c.client_id})`, value: String(c.client_id) })));
                setAllBranches(toArray(branchRes).map((b) => ({ label: `${b.branch_name} (${b.branch_id})`, value: String(b.branch_id), clientId: String(b.client_id) })));
            } catch (error) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load clients/branches' });
            } finally {
                setLoadingLookups(false);
            }
        };
        loadLookups();
    }, []);

    const branchOptions = allBranches.filter((b) => b.clientId === client);
    const selectedClientLabel = clients.find((c) => c.value === client)?.label;

    const resetPushResults = () => {
        setPushPreviewRows(null);
        setPushConnectError(null);
        setPushRows(null);
        setPushProgress(null);
    };
    const resetPullResults = () => {
        setPullPreviewRows(null);
        setPullConnectError(null);
        setPullRows(null);
        setPullProgress(null);
    };

    const onClientChange = (value) => {
        setClient(value);
        setPushBranch(null);
        setPullBranch(null);
        resetPushResults();
        resetPullResults();
    };
    const onPushBranchChange = (value) => {
        setPushBranch(value);
        resetPushResults();
    };
    const onPullBranchChange = (value) => {
        setPullBranch(value);
        resetPullResults();
    };
    const togglePushTable = (value) => {
        setPushSelectedTables((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]));
        resetPushResults();
    };
    const togglePushAll = () => {
        const allOn = PUSH_TABLE_VALUES.every((t) => pushSelectedTables.includes(t));
        setPushSelectedTables(allOn ? [] : PUSH_TABLE_VALUES);
        resetPushResults();
    };
    const togglePullTable = (value) => {
        setPullSelectedTables((prev) => (prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]));
        resetPullResults();
    };
    const togglePullAll = () => {
        const allOn = PULL_TABLE_VALUES.every((t) => pullSelectedTables.includes(t));
        setPullSelectedTables(allOn ? [] : PULL_TABLE_VALUES);
        resetPullResults();
    };

    const pushReadyToCheck = client && pushSelectedTables.length > 0;
    const pushCurrentKey = () => JSON.stringify({ client, pushBranch, pushSelectedTables: [...pushSelectedTables].sort() });

    useEffect(() => {
        if (!pushReadyToCheck) return undefined;
        let cancelled = false;
        const run = async () => {
            setPushPreviewLoading(true);
            try {
                const payload = { client_id: Number(client), tables: pushSelectedTables };
                if (pushBranch) payload.branch_id = Number(pushBranch);
                const result = await previewDataPushing(payload);
                if (cancelled) return;
                if (result?.status === 'success') {
                    setPushPreviewRows(result.tables || []);
                    setPushConnectError(result.connect_error || null);
                    setPushPreviewKey(pushCurrentKey());
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Push check failed', detail: result?.message || 'Unknown error' });
                }
            } catch (error) {
                if (!cancelled) toast.current?.show({ severity: 'error', summary: 'Push check failed', detail: error?.response?.data?.message || error.message });
            } finally {
                if (!cancelled) setPushPreviewLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, pushBranch, pushSelectedTables.join(',')]);

    const pullReadyToCheck = client && pullSelectedTables.length > 0;
    const pullCurrentKey = () => JSON.stringify({ client, pullBranch, pullSelectedTables: [...pullSelectedTables].sort() });

    useEffect(() => {
        if (!pullReadyToCheck) return undefined;
        let cancelled = false;
        const run = async () => {
            setPullPreviewLoading(true);
            try {
                const payload = { client_id: Number(client), tables: pullSelectedTables };
                if (pullBranch) payload.branch_id = Number(pullBranch);
                const result = await previewDataPulling(payload);
                if (cancelled) return;
                if (result?.status === 'success') {
                    setPullPreviewRows(result.tables || []);
                    setPullConnectError(result.connect_error || null);
                    setPullPreviewKey(pullCurrentKey());
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Pull check failed', detail: result?.message || 'Unknown error' });
                }
            } catch (error) {
                if (!cancelled) toast.current?.show({ severity: 'error', summary: 'Pull check failed', detail: error?.response?.data?.message || error.message });
            } finally {
                if (!cancelled) setPullPreviewLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, pullBranch, pullSelectedTables.join(',')]);

    const pushPreviewStale = pushPreviewRows !== null && pushPreviewKey !== pushCurrentKey();
    const pullPreviewStale = pullPreviewRows !== null && pullPreviewKey !== pullCurrentKey();
    const totalServerRowsToPush = pushPreviewRows ? pushPreviewRows.reduce((s, r) => s + (r.source_count || 0), 0) : null;
    const totalClientRows = pullPreviewRows ? pullPreviewRows.reduce((s, r) => s + (r.source_count || 0), 0) : null;
    const totalServerRows = pullPreviewRows ? pullPreviewRows.reduce((s, r) => s + (r.dest_count || 0), 0) : null;

    const runPush = async (tables) => {
        if (tables.length === 0) return;

        setPushLoading(true);
        setPushRows([]);
        const collected = [];

        try {
            for (let i = 0; i < tables.length; i++) {
                const table = tables[i];
                setPushProgress({ current: i, total: tables.length, label: `Pushing ${TABLE_META[table]?.label || table}…`, icon: TABLE_META[table]?.icon, color: PUSH_COLOR });

                const payload = { client_id: Number(client), tables: [table] };
                if (pushBranch) payload.branch_id = Number(pushBranch);
                // eslint-disable-next-line no-await-in-loop
                const result = await runDataPushing(payload);
                const rows =
                    result?.status === 'success'
                        ? result.results || []
                        : [{ table, status: 'error', created_table: false, inserted: 0, updated: 0, message: result?.message || 'Request failed' }];

                collected.push(...rows);
                setPushRows([...collected]);
                const next = tables[i + 1];
                setPushProgress({
                    current: i + 1,
                    total: tables.length,
                    label: next ? `Pushing ${TABLE_META[next]?.label || next}…` : 'Done',
                    icon: next ? TABLE_META[next]?.icon : 'pi-check-circle',
                    color: PUSH_COLOR
                });
            }

            const failed = collected.some((r) => r.status === 'error');
            toast.current?.show({
                severity: failed ? 'warn' : 'success',
                summary: 'Push To Client finished',
                detail: failed ? 'Completed with some tables failed — see results below' : "Data pushed onto the client's own database"
            });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Push failed', detail: error?.response?.data?.message || error.message });
        } finally {
            setPushLoading(false);
        }
    };

    const runPull = async (tables) => {
        if (tables.length === 0) return;

        setPullLoading(true);
        setPullRows([]);
        const collected = [];

        try {
            for (let i = 0; i < tables.length; i++) {
                const table = tables[i];
                setPullProgress({ current: i, total: tables.length, label: `Pulling ${TABLE_META[table]?.label || table}…`, icon: TABLE_META[table]?.icon, color: PULL_COLOR });

                const payload = { client_id: Number(client), tables: [table] };
                if (pullBranch) payload.branch_id = Number(pullBranch);
                // eslint-disable-next-line no-await-in-loop
                const result = await runDataPulling(payload);
                const rows =
                    result?.status === 'success' ? result.results || [] : [{ table, status: 'error', inserted: 0, updated: 0, message: result?.message || 'Request failed' }];

                collected.push(...rows);
                setPullRows([...collected]);
                const next = tables[i + 1];
                setPullProgress({
                    current: i + 1,
                    total: tables.length,
                    label: next ? `Pulling ${TABLE_META[next]?.label || next}…` : 'Done',
                    icon: next ? TABLE_META[next]?.icon : 'pi-check-circle',
                    color: PULL_COLOR
                });
            }

            const failed = collected.some((r) => r.status === 'error');
            toast.current?.show({
                severity: failed ? 'warn' : 'success',
                summary: 'Pull From Client finished',
                detail: failed ? 'Completed with some tables failed — see results below' : 'Data pulled onto the server database'
            });
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Pull failed', detail: error?.response?.data?.message || error.message });
        } finally {
            setPullLoading(false);
        }
    };

    const confirmPush = () => {
        const tables = PUSH_TABLE_VALUES.filter((v) => pushSelectedTables.includes(v));
        if (tables.length === 0) return;
        const branchLabel = pushBranch ? branchOptions.find((b) => b.value === pushBranch)?.label : null;
        const hasScopedOnly = tables.some((v) => !CLIENT_TABLES_VALUES.includes(v) && !GLOBAL_GROUP.tables.some((t) => t.value === v));

        confirmDialog({
            header: 'Push To Client?',
            message: (
                <p>
                    For client {client}
                    {branchLabel ? `, branch ${branchLabel}` : ' (all branches)'}, this reads the selected tables from the <strong>server</strong> (master) database and writes them onto that
                    client&apos;s OWN database: creates the table there first if it doesn&apos;t exist yet, then matches rows by id — existing ids on the client are overwritten with the
                    server&apos;s current values, ids that don&apos;t exist yet are inserted. Nothing is deleted.
                    {branchLabel && hasScopedOnly ? ' Global Masters and Client/Branch/Category Master rows have no single-branch scope, so they are still pushed in full regardless of the branch picked.' : ''}
                </p>
            ),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Push',
            rejectLabel: 'Cancel',
            accept: () => runPush(tables)
        });
    };

    const confirmPull = () => {
        const tables = PULL_TABLE_VALUES.filter((v) => pullSelectedTables.includes(v));
        if (tables.length === 0) return;
        const branchLabel = pullBranch ? branchOptions.find((b) => b.value === pullBranch)?.label : null;
        const hasClientTables = tables.some((v) => CLIENT_TABLES_VALUES.includes(v));

        confirmDialog({
            header: 'Pull From Client?',
            message: (
                <p>
                    For client {client}
                    {branchLabel ? `, branch ${branchLabel}` : ' (all branches)'}, this reads the selected tables from that client&apos;s OWN database and writes them onto the <strong>server</strong>{' '}
                    database: matches rows by id — existing ids on the server are overwritten with the client&apos;s current values, ids that don&apos;t exist yet are inserted. Nothing is deleted.
                    {branchLabel && hasClientTables ? ' Client, Branch and Category Master rows have no single-branch scope, so they are still pulled in full for this client regardless of the branch picked.' : ''}
                </p>
            ),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Pull',
            rejectLabel: 'Cancel',
            accept: () => runPull(tables)
        });
    };

    const canPush = client && pushSelectedTables.length > 0 && !pushLoading;
    const canPull = client && pullSelectedTables.length > 0 && !pullLoading && !pullConnectError;

    const pushSummary = pushRows && {
        pushed: pushRows.filter((r) => r.status === 'pushed').length,
        skipped: pushRows.filter((r) => r.status !== 'pushed').length,
        created: pushRows.filter((r) => r.created_table).length,
        inserted: pushRows.reduce((s, r) => s + (r.inserted || 0), 0),
        updated: pushRows.reduce((s, r) => s + (r.updated || 0), 0)
    };
    const pullSummary = pullRows && {
        pulled: pullRows.filter((r) => r.status === 'pulled').length,
        skipped: pullRows.filter((r) => r.status !== 'pulled').length,
        inserted: pullRows.reduce((s, r) => s + (r.inserted || 0), 0),
        updated: pullRows.reduce((s, r) => s + (r.updated || 0), 0)
    };

    return (
        <div className="dp-page">
            <style>{`
                .dp-tile { position: relative; display:flex; flex-direction:column; align-items:flex-start; gap:.5rem; padding:.75rem .9rem; border-radius:12px; border:1px solid var(--surface-border); background:var(--surface-card); cursor:pointer; transition: all .15s ease; min-width:132px; }
                .dp-tile:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,.08); }
                .dp-tile.active { border-color: var(--tile-color); background: color-mix(in srgb, var(--tile-color) 10%, var(--surface-card)); }
                .dp-tile .dp-check { position:absolute; top:6px; right:6px; color: var(--tile-color); }
                .dp-tile .dp-icon { font-size:1.1rem; color: var(--tile-color); }
                .dp-row-ok { background: color-mix(in srgb, #22c55e 6%, transparent); }
                .dp-row-bad { background: color-mix(in srgb, #ef4444 7%, transparent); }
                .dp-row-muted { opacity: .65; }
                .dp-pulse { animation: dpPulse 1.4s ease-in-out infinite; }
                @keyframes dpPulse { 0%,100% { opacity: 1; } 50% { opacity: .55; } }
                .dp-card .p-card-body { padding: 1rem 1.25rem; }
                .dp-card .p-card-content { padding: 0; }
                .dp-header { padding-bottom: .9rem; border-bottom: 1px solid var(--surface-border); margin-bottom: 1rem; }
                .dp-overview { display:flex; flex-wrap:wrap; align-items:stretch; gap: .75rem; margin-bottom: 1rem; }
                .dp-client-box { flex: 1 1 280px; max-width: 320px; }
                .dp-stats-row { flex: 2 1 420px; display:flex; flex-wrap:wrap; gap:.75rem; }
                .dp-stats-row .dp-stat { flex: 1 1 140px; }
                .dp-section { border-radius: 12px; border: 1px solid var(--surface-border); border-left-width: 4px; border-left-style: solid; background: var(--surface-card); overflow: hidden; transition: box-shadow .15s ease, border-color .15s ease; }
                .dp-section:hover { box-shadow: 0 2px 12px rgba(0,0,0,.06); }
                .dp-section-header { padding: .6rem 1rem; border-bottom: 1px solid var(--surface-border); background: var(--surface-50); }
                .dp-section-body { padding: 1rem; animation: dpFadeIn .15s ease; }
                .dp-sections { display:flex; flex-direction:column; gap: .75rem; }
                @keyframes dpFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Card className="dp-card">
                <div className="dp-header flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center justify-content-center border-circle flex-shrink-0" style={{ width: 44, height: 44, background: `linear-gradient(135deg,#0ea5e9,#8b5cf6)` }}>
                            <i className="pi pi-sync text-white text-xl" />
                        </div>
                        <div>
                            <div className="text-xl font-bold line-height-2">Data Pushing — Sync Server &amp; Client</div>
                            <div className="text-color-secondary text-sm">Push a selected client&apos;s data from the server onto its own database, or pull the client&apos;s own data back onto the server.</div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Badge value={`${PUSH_TABLE_VALUES.length} pushable`} style={{ backgroundColor: PUSH_COLOR, fontSize: '0.8rem' }} />
                        <Badge value={`${PULL_TABLE_VALUES.length} pullable`} style={{ backgroundColor: PULL_COLOR, fontSize: '0.8rem' }} />
                    </div>
                </div>

                <div className="dp-overview">
                    <div className="dp-client-box border-round shadow-1 p-3 surface-card" style={{ borderTop: `4px solid ${PUSH_COLOR}` }}>
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-building" style={{ color: PUSH_COLOR }} />
                            <span className="font-bold">Client</span>
                        </div>
                        <Dropdown
                            value={client}
                            options={clients}
                            onChange={(e) => onClientChange(e.value)}
                            placeholder="Select client"
                            filter
                            showClear
                            className="w-full"
                            loading={loadingLookups}
                        />
                        {client && (
                            <div className="flex align-items-center gap-2 mt-3">
                                <Avatar label={initials(selectedClientLabel)} shape="circle" style={{ backgroundColor: PUSH_COLOR, color: '#fff' }} />
                                <span className="text-sm">{selectedClientLabel}</span>
                            </div>
                        )}
                    </div>
                    <div className="dp-stats-row">
                        <StatTile label="Server Rows To Push" value={totalServerRowsToPush ?? '—'} color={PUSH_COLOR} icon="pi-cloud-upload" />
                        <StatTile label="Client Rows To Pull" value={totalClientRows ?? '—'} color={PULL_COLOR} icon="pi-cloud-download" />
                        <StatTile label="Rows On Server Now" value={totalServerRows ?? '—'} color="#6366f1" icon="pi-server" />
                    </div>
                </div>

                {!client && (
                    <div className="flex flex-column align-items-center justify-content-center text-center border-round surface-50 mb-3" style={{ padding: '2rem 1rem' }}>
                        <i className="pi pi-arrow-up text-3xl text-color-secondary mb-2" />
                        <div className="text-lg font-medium">Select a client above to get started</div>
                        <div className="text-color-secondary text-sm mt-1">Once a client is picked, both directions below show what&apos;s ready to sync.</div>
                    </div>
                )}

                <div className="dp-sections">
                    <div className="dp-section" style={{ borderLeftColor: PUSH_COLOR }}>
                        <div className="dp-section-header flex flex-wrap align-items-center justify-content-between gap-2">
                            <span className="flex align-items-center gap-2">
                                <Button
                                    icon={`pi ${collapsed.push ? 'pi-chevron-right' : 'pi-chevron-down'}`}
                                    text
                                    rounded
                                    size="small"
                                    aria-label="Toggle section"
                                    onClick={() => toggleCollapsed('push')}
                                />
                                <i className="pi pi-cloud-upload" style={{ color: PUSH_COLOR }} />
                                <span className="font-bold" style={{ color: PUSH_COLOR }}>
                                    Server → Client (Push)
                                </span>
                                <Badge value={`${pushSelectedTables.length}/${PUSH_TABLE_VALUES.length}`} style={{ backgroundColor: PUSH_COLOR }} />
                            </span>
                            <Button
                                label="Push To Client"
                                icon="pi pi-cloud-upload"
                                size="small"
                                loading={pushLoading}
                                disabled={!canPush}
                                onClick={confirmPush}
                            />
                        </div>
                        {!collapsed.push && (
                            <div className="dp-section-body">
                                <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <p className="text-color-secondary text-sm m-0">
                                        Reads from the server (master) database and writes onto the client&apos;s own database — creating the table there first if it doesn&apos;t exist yet. The
                                        server&apos;s current values win on conflict.
                                    </p>
                                    <Button label={PUSH_TABLE_VALUES.every((t) => pushSelectedTables.includes(t)) ? 'Clear all' : 'Select all'} link size="small" onClick={togglePushAll} />
                                </div>

                                <div className="mb-3" style={{ maxWidth: 320 }}>
                                    <label className="block text-sm font-medium mb-1">Branch (optional)</label>
                                    <Dropdown
                                        value={pushBranch}
                                        options={branchOptions}
                                        onChange={(e) => onPushBranchChange(e.value)}
                                        placeholder={client ? 'All branches' : 'Select a client first'}
                                        filter
                                        showClear
                                        disabled={!client}
                                        className="w-full"
                                    />
                                    {client && !pushBranch && <small className="text-color-secondary">No branch picked — every branch of this client will be pushed.</small>}
                                </div>

                                {pushConnectError && <Message severity="warn" className="mb-3 w-full" text={`${pushConnectError} — tables will still be created/pushed once a database is configured for this client.`} />}

                                {PUSH_GROUPS.map((group, idx) => (
                                    <div key={group.key} className={idx > 0 ? 'mt-3' : ''}>
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <span className="text-sm font-medium">{group.title}</span>
                                            {group.needsBranch ? (
                                                <Tag severity="info" value="branch-aware" style={{ fontSize: '0.65rem' }} />
                                            ) : (
                                                <Tag severity="secondary" value="always full client" style={{ fontSize: '0.65rem' }} />
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {group.tables.map((t) => {
                                                const active = pushSelectedTables.includes(t.value);
                                                return (
                                                    <div
                                                        key={t.value}
                                                        className={`dp-tile ${active ? 'active' : ''}`}
                                                        style={{ '--tile-color': PUSH_COLOR }}
                                                        onClick={() => togglePushTable(t.value)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        {active && <i className="pi pi-check-circle dp-check" />}
                                                        <i className={`pi ${t.icon} dp-icon`} />
                                                        <span className="text-sm font-medium">{t.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="dp-section" style={{ borderLeftColor: PULL_COLOR }}>
                        <div className="dp-section-header flex flex-wrap align-items-center justify-content-between gap-2">
                            <span className="flex align-items-center gap-2">
                                <Button
                                    icon={`pi ${collapsed.pull ? 'pi-chevron-right' : 'pi-chevron-down'}`}
                                    text
                                    rounded
                                    size="small"
                                    aria-label="Toggle section"
                                    onClick={() => toggleCollapsed('pull')}
                                />
                                <i className="pi pi-cloud-download" style={{ color: PULL_COLOR }} />
                                <span className="font-bold" style={{ color: PULL_COLOR }}>
                                    Client → Server (Pull)
                                </span>
                                <Badge value={`${pullSelectedTables.length}/${PULL_TABLE_VALUES.length}`} style={{ backgroundColor: PULL_COLOR }} />
                            </span>
                            <Button
                                label="Pull From Client"
                                icon="pi pi-cloud-download"
                                size="small"
                                severity="help"
                                loading={pullLoading}
                                disabled={!canPull}
                                onClick={confirmPull}
                            />
                        </div>
                        {!collapsed.pull && (
                            <div className="dp-section-body">
                                <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <p className="text-color-secondary text-sm m-0">
                                        Reads from the client&apos;s own database and writes onto the server database. Global Masters stay server-authoritative and aren&apos;t pullable; the
                                        client&apos;s current values win on conflict.
                                    </p>
                                    <Button label={PULL_TABLE_VALUES.every((t) => pullSelectedTables.includes(t)) ? 'Clear all' : 'Select all'} link size="small" onClick={togglePullAll} />
                                </div>

                                <div className="mb-3" style={{ maxWidth: 320 }}>
                                    <label className="block text-sm font-medium mb-1">Branch (optional)</label>
                                    <Dropdown
                                        value={pullBranch}
                                        options={branchOptions}
                                        onChange={(e) => onPullBranchChange(e.value)}
                                        placeholder={client ? 'All branches' : 'Select a client first'}
                                        filter
                                        showClear
                                        disabled={!client}
                                        className="w-full"
                                    />
                                    {client && !pullBranch && <small className="text-color-secondary">No branch picked — every branch of this client will be pulled.</small>}
                                </div>

                                {pullConnectError && <Message severity="error" className="mb-3 w-full" text={pullConnectError} />}

                                {PULL_GROUPS.map((group, idx) => (
                                    <div key={group.key} className={idx > 0 ? 'mt-3' : ''}>
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <span className="text-sm font-medium">{group.title}</span>
                                            {group.needsBranch ? (
                                                <Tag severity="info" value="branch-aware" style={{ fontSize: '0.65rem' }} />
                                            ) : (
                                                <Tag severity="secondary" value="always full client" style={{ fontSize: '0.65rem' }} />
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {group.tables.map((t) => {
                                                const active = pullSelectedTables.includes(t.value);
                                                return (
                                                    <div
                                                        key={t.value}
                                                        className={`dp-tile ${active ? 'active' : ''}`}
                                                        style={{ '--tile-color': PULL_COLOR }}
                                                        onClick={() => togglePullTable(t.value)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        {active && <i className="pi pi-check-circle dp-check" />}
                                                        <i className={`pi ${t.icon} dp-icon`} />
                                                        <span className="text-sm font-medium">{t.label}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {pushProgress && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PUSH_COLOR }}>
                        <div className="dp-section-body">
                            <div className="flex justify-content-between align-items-center text-sm mb-2">
                                <span className="flex align-items-center gap-2">
                                    <i className={`pi ${pushProgress.icon || 'pi-spin pi-spinner'} ${pushLoading ? 'dp-pulse' : ''}`} style={{ color: PUSH_COLOR }} />
                                    <span className="font-medium">{pushProgress.label}</span>
                                </span>
                                <span className="text-color-secondary">
                                    {pushProgress.current}/{pushProgress.total}
                                </span>
                            </div>
                            <ProgressBar value={Math.round((pushProgress.current / pushProgress.total) * 100)} showValue={false} color={PUSH_COLOR} style={{ height: 10 }} />
                        </div>
                    </div>
                )}

                {pullProgress && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PULL_COLOR }}>
                        <div className="dp-section-body">
                            <div className="flex justify-content-between align-items-center text-sm mb-2">
                                <span className="flex align-items-center gap-2">
                                    <i className={`pi ${pullProgress.icon || 'pi-spin pi-spinner'} ${pullLoading ? 'dp-pulse' : ''}`} style={{ color: PULL_COLOR }} />
                                    <span className="font-medium">{pullProgress.label}</span>
                                </span>
                                <span className="text-color-secondary">
                                    {pullProgress.current}/{pullProgress.total}
                                </span>
                            </div>
                            <ProgressBar value={Math.round((pullProgress.current / pullProgress.total) * 100)} showValue={false} color={PULL_COLOR} style={{ height: 10 }} />
                        </div>
                    </div>
                )}

                {pushPreviewLoading && <Message severity="info" className="mt-3 w-full" text="Checking server database for rows to push..." />}
                {pushPreviewStale && <Message severity="warn" className="mt-3 w-full" text="Selection changed — re-checking automatically." />}

                {pushPreviewRows && !pushPreviewLoading && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PUSH_COLOR }}>
                        <div className="dp-section-header">
                            <span className="font-bold" style={{ color: PUSH_COLOR }}>
                                Push Preview{pushBranch ? ` — branch ${branchOptions.find((b) => b.value === pushBranch)?.label || pushBranch}` : ' — all branches'}
                            </span>
                        </div>
                        <div className="dp-section-body">
                            <DataTable value={pushPreviewRows} size="small" stripedRows className="border-round overflow-hidden">
                                <Column
                                    header="Table"
                                    body={(row) => (
                                        <span className="flex align-items-center gap-2">
                                            <i className={`pi ${TABLE_META[row.table]?.icon}`} style={{ color: TABLE_META[row.table]?.color }} />
                                            {TABLE_META[row.table]?.label || row.table}
                                        </span>
                                    )}
                                />
                                <Column field="source_count" header="Server Rows" />
                                <Column header="Exists On Client" body={(row) => <Tag severity={row.exists_on_client ? 'success' : 'secondary'} value={row.exists_on_client ? 'Yes' : 'Not yet'} />} />
                                <Column field="dest_count" header="Client Rows Now" />
                            </DataTable>
                        </div>
                    </div>
                )}

                {pushRows && pushRows.length > 0 && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PUSH_COLOR }}>
                        <div className="dp-section-header">
                            <span className="font-bold" style={{ color: PUSH_COLOR }}>
                                Push Result
                            </span>
                        </div>
                        <div className="dp-section-body">
                            {pushSummary && !pushLoading && (
                                <div className="flex flex-wrap gap-3 mb-3">
                                    <StatTile label="Tables pushed" value={pushSummary.pushed} color={PUSH_COLOR} icon="pi-check-circle" />
                                    <StatTile label="Tables created" value={pushSummary.created} color="#6366f1" icon="pi-plus" />
                                    <StatTile label="Rows inserted" value={pushSummary.inserted} color="#22c55e" icon="pi-plus-circle" />
                                    <StatTile label="Rows overwritten" value={pushSummary.updated} color="#f59e0b" icon="pi-sync" />
                                </div>
                            )}
                            <DataTable value={pushRows} size="small" stripedRows className="border-round overflow-hidden" rowClassName={(row) => PUSH_ROW_CLASS[row.status] || ''}>
                                <Column field="table" header="Table" body={(row) => TABLE_META[row.table]?.label || row.table} />
                                <Column header="Status" body={(row) => <Tag severity={PUSH_STATUS_SEVERITY[row.status] || 'info'} value={row.status} />} />
                                <Column header="Table Created" body={(row) => (row.created_table ? <Tag severity="info" value="Created" /> : '')} />
                                <Column field="inserted" header="Inserted" />
                                <Column field="updated" header="Overwritten" />
                                <Column field="message" header="Message" />
                            </DataTable>
                        </div>
                    </div>
                )}

                {pullPreviewLoading && <Message severity="info" className="mt-3 w-full" text="Checking client database for rows to pull..." />}
                {pullPreviewStale && <Message severity="warn" className="mt-3 w-full" text="Selection changed — re-checking automatically." />}

                {pullPreviewRows && !pullPreviewLoading && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PULL_COLOR }}>
                        <div className="dp-section-header">
                            <span className="font-bold" style={{ color: PULL_COLOR }}>
                                Pull Preview{pullBranch ? ` — branch ${branchOptions.find((b) => b.value === pullBranch)?.label || pullBranch}` : ' — all branches'}
                            </span>
                        </div>
                        <div className="dp-section-body">
                            <DataTable value={pullPreviewRows} size="small" stripedRows className="border-round overflow-hidden">
                                <Column
                                    header="Table"
                                    body={(row) => (
                                        <span className="flex align-items-center gap-2">
                                            <i className={`pi ${TABLE_META[row.table]?.icon}`} style={{ color: TABLE_META[row.table]?.color }} />
                                            {TABLE_META[row.table]?.label || row.table}
                                        </span>
                                    )}
                                />
                                <Column field="source_count" header="Client Rows" />
                                <Column field="dest_count" header="Server Rows Now" />
                            </DataTable>
                        </div>
                    </div>
                )}

                {pullRows && pullRows.length > 0 && (
                    <div className="dp-section mt-3" style={{ borderLeftColor: PULL_COLOR }}>
                        <div className="dp-section-header">
                            <span className="font-bold" style={{ color: PULL_COLOR }}>
                                Pull Result
                            </span>
                        </div>
                        <div className="dp-section-body">
                            {pullSummary && !pullLoading && (
                                <div className="flex flex-wrap gap-3 mb-3">
                                    <StatTile label="Tables pulled" value={pullSummary.pulled} color={PULL_COLOR} icon="pi-check-circle" />
                                    <StatTile label="Rows inserted" value={pullSummary.inserted} color="#6366f1" icon="pi-plus-circle" />
                                    <StatTile label="Rows overwritten" value={pullSummary.updated} color="#f59e0b" icon="pi-sync" />
                                </div>
                            )}
                            <DataTable value={pullRows} size="small" stripedRows className="border-round overflow-hidden" rowClassName={(row) => PULL_ROW_CLASS[row.status] || ''}>
                                <Column field="table" header="Table" body={(row) => TABLE_META[row.table]?.label || row.table} />
                                <Column header="Status" body={(row) => <Tag severity={PULL_STATUS_SEVERITY[row.status] || 'info'} value={row.status} />} />
                                <Column field="inserted" header="Inserted" />
                                <Column field="updated" header="Overwritten" />
                                <Column field="message" header="Message" />
                            </DataTable>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Datapushing;
